import { useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { MdClose, MdFormatQuote } from 'react-icons/md';
import { SlUserUnfollow } from 'react-icons/sl';

import { Button } from '../Button';
import { InputComponent } from '../form/InputComponent';
import { Modal } from '../modal/Modal';
import { Initial } from '../initials/Initial';

import { selectActiveAccount, selectUser, selectAllAccounts, setAccount } from '../../reducers/account.reducer';
import { selectCurrentEntity, selectCurrentStep, selectCurrentSurvey, selectCurrentVersion, selectShareModal } from '../../reducers/app.reducer';

import { addEventTracking, isEmailValid } from '../../utils/utils';
import { shareItemToUser } from '../../utils/api';
import { COLORS } from '../../utils/constants';
import { toast } from 'react-toastify';

interface ModalShareProps {
  type: string;
  id: string;
  label: string;
  onClose: () => void;
  loading: (boolean) => void;
  targetSurveyId?: string;
  relatedProductId?: string;
  show?: boolean;
}

export const ModalShare = ({ type, id, label, onClose, loading, targetSurveyId, relatedProductId, show }: ModalShareProps) => {
  // REDUX
  const dispatch = useDispatch<any>();
  const allAccounts = useSelector(selectAllAccounts);
  const activeAccount = useSelector(selectActiveAccount);
  const currentEntity = useSelector(selectCurrentEntity);
  const currentSurvey = useSelector(selectCurrentSurvey);
  const currentVersion = useSelector(selectCurrentVersion);
  const currentStep = useSelector(selectCurrentStep);
  const host = useSelector(selectUser);
  const shareModal = useSelector(selectShareModal);
  // STATE
  const [usersChecked, setUsersChecked] = useState([]);
  const [emailInputList, setEmailInputList] = useState([]);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailAlreadyUsed, setEmailAlreadyUsed] = useState(false);

  const handleSelectUser = user => {
    if (!isChecked(user)) {
      setUsersChecked([...usersChecked, user]);
    } else {
      let usersCheckedCopy = usersChecked.filter(userCopy => userCopy !== user);
      setUsersChecked(usersCheckedCopy);
    }
  };
  const handleEmailChange = e => {
    setEmailInputValue(e.target.value);
    setEmailError(false);
    setEmailAlreadyUsed(false);
  };
  const handleCommentChange = e => {
    setCommentInput(e.target.value);
  };
  const removeEmail = emailToDelete => {
    setEmailInputList(emailInputList.filter(email => email !== emailToDelete));
  };
  const handleEmailValidation = e => {
    if (emailInputValue === '') return;
    if (['Enter', 'Tab', ','].includes(e.key) || e.type === 'blur') {
      e.preventDefault();
      let email = emailInputValue.trim();
      let error = !isEmailValid(email) || isEmailAlreadyUsed(email);
      if (!isEmailValid(email)) {
        setEmailError(true);
      }
      if (isEmailAlreadyUsed(email)) {
        setEmailAlreadyUsed(true);
      }
      if (email && !error) {
        setEmailInputList([...emailInputList, email]);
        setEmailInputValue('');
        setEmailError(false);
        setEmailAlreadyUsed(false);
      }
    }
  };

  const handleShare = async () => {
    let toastLabel = '';
    const sharedItemInformations = {
      currentStep: currentStep,
      currentVersion: currentVersion,
      currentSurvey: currentSurvey,
      currentEntity: currentEntity,
      itemId: id,
    };

    switch (type) {
      case 'questions':
        addEventTracking('Share', 'Question');
        toastLabel = 'Votre question a bien été partagée';
        break;
      case 'surveySteps':
        addEventTracking('Share', 'Step');
        toastLabel = 'Le thème a bien été partagée';
        break;
      case 'surveyVersions':
        addEventTracking('Share', 'Survey');
        toastLabel = 'Le questionnaire a bien été partagé';
        break;
      case 'surveys':
        break;
      case 'entities':
        addEventTracking('Share', 'Entity');
        toastLabel = "L'entité a bien été partagée";
        break;
      case 'actions':
        addEventTracking('Share', 'Action');
        toastLabel = "L'action a bien été partagée";

        break;
      default:
        break;
    }

    let result;

    for (const email of emailInputList) {
      let newUserObject = {
        accountOwner: false,
        activity: {
          lastSaved: null,
          sentMails: [],
        },
        name: '',
        email,
        receivesEmails: true,
        sharedItems: { entities: [], surveys: [], surveyVersions: [], surveySteps: [], questions: [], actions: [] },
        wizardCompleted: true,
        role: 'guest',
      };

      result = await shareItemToUser(
        activeAccount,
        host,
        commentInput,
        type,
        sharedItemInformations,
        targetSurveyId,
        relatedProductId,
        null,
        newUserObject
      );
    }

    for (const guest of usersChecked) {
      result = await shareItemToUser(activeAccount, host, commentInput, type, sharedItemInformations, targetSurveyId, relatedProductId, guest);
    }

    dispatch(setAccount(result.updatedAccount));

    toast.info(toastLabel);
    setUsersChecked([]);
    setEmailInputList([]);
    onClose();
    return;
  };

  const isItemShared = (user, itemType, shareId) => {
    for (let shareType in user.sharedItems) {
      if (shareType === '_id') continue;
      if (
        itemType === 'actions' &&
        user.sharedItems[shareType].find(shareItem => shareItem?.shareId === shareId && shareItem.entityId === currentEntity?._id)
      ) {
        return true;
      }
      if (user.sharedItems[shareType].find(shareItem => shareItem?.shareId?.includes(shareId) && shareItem.entityId === currentEntity?._id)) {
        return true;
      }
      if (
        user.sharedItems[shareType].find(
          shareItem => shareItem?.[getItemKey(itemType)]?.includes(shareId) && shareItem.entityId === currentEntity?._id
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const isFullyShared = (user, itemType) => {
    return user?.sharedItems?.[itemType]?.find(
      shareItem => shareItem?.[getItemKey(itemType)] === 'all' && shareItem?.entityId === currentEntity?._id
    );
  };

  const isAlreadyShared = (user, itemType, shareId) => {
    if (isItemShared(user, itemType, shareId)) return true;
    if (!isFullyShared(user, itemType)) return false;
    switch (itemType) {
      case 'questions':
        return isAlreadyShared(user, 'surveySteps', currentStep._id);
      case 'surveySteps':
        return isAlreadyShared(user, 'surveyVersions', currentVersion._id);
      case 'surveyVersions':
        return isAlreadyShared(user, 'surveys', currentSurvey._id);
      case 'surveys':
        return isAlreadyShared(user, 'entities', currentEntity._id);
      case 'actions':
        return isAlreadyShared(user, 'entities', currentEntity._id);
      case 'entities':
        return false;
    }
  };

  const getItemKey = itemType => {
    switch (itemType) {
      case 'questions':
        return 'questionId';
      case 'surveySteps':
        return 'surveyStepId';
      case 'surveyVersions':
        return 'surveyVersionId';
      case 'surveys':
        return 'surveyId';
      case 'entities':
        return 'entityId';
      case 'actions':
        return 'actionId';
      default:
        return '';
    }
  };

  const isEmailAlreadyUsed = email => {
    if (email === '') return false;
    let emailFound = false;
    allAccounts.forEach(account => {
      if (account.users.some(user => user.email === email)) {
        emailFound = true;
      }
    });
    if (emailInputList.some(emailCheck => emailCheck === email)) {
      emailFound = true;
    }
    return emailFound;
  };

  const isChecked = user => {
    return usersChecked.some(userCopy => userCopy === user);
  };

  const getTypeLabel = itemType => {
    switch (itemType) {
      case 'questions':
        return 'question';
      case 'surveySteps':
        return 'étape';
      case 'surveyVersions':
        return 'questionnaire';
      case 'surveys':
        return 'questionnaire';
      case 'entities':
        return 'entité';
      case 'actions':
        return 'action';
    }
  };

  const getShareTitle = shareTitle => {
    switch (shareTitle) {
      case 'entities':
        return `${shareModal?.label}`;
      case 'surveyVersions':
        return `${shareModal?.label}`;
      case 'surveySteps':
        return `${shareModal?.label}`;
      case 'questions':
        return `Une question`;
      case 'actions':
        return `l'Action : ${shareModal?.label}`;
    }
  };

  return (
    <>
      {show && (
        <Modal show={show} title={`Partager <b>${getShareTitle(shareModal.type)}</b>`} onClose={onClose} size={{ width: '50%' }}>
          <ModalShareContainer>
            <>
              {type === 'questions' && (
                <LabelContainer>
                  <MdFormatQuote />
                  {label} <MdFormatQuote />
                </LabelContainer>
              )}
              <InputContainer>
                <ChipsContainer>
                  {emailInputList?.map(
                    (email, index) =>
                      email && (
                        <Chips key={index}>
                          {email}
                          <ChipsButton onClick={() => removeEmail(email)}>
                            <MdClose />
                          </ChipsButton>
                        </Chips>
                      )
                  )}
                </ChipsContainer>
                <InputComponent
                  type={'text'}
                  placeholder={"Ajoutez l'adresse mail des collaborateurs que vous souhaitez solliciter"}
                  onChange={handleEmailChange}
                  onKeyDown={handleEmailValidation}
                  onBlur={handleEmailValidation}
                  value={emailInputValue}
                />
                {(emailError || emailAlreadyUsed) && (
                  <EmailError>
                    {' '}
                    {emailInputValue} {emailError ? `n'est pas une adresse mail valide` : `est déjà utilisé`}
                  </EmailError>
                )}
              </InputContainer>
              {activeAccount?.users.filter(user => user.role === 'guest').filter(user => !isAlreadyShared(user, type, id)).length > 0 ? (
                <span>Utilisateurs du compte </span>
              ) : activeAccount.users.length > 1 ? (
                <Empty>
                  <span>
                    Vous avez{' '}
                    <b>
                      déjà partagé ce{type !== 'surveyVersions' ? 'tte' : ''} {getTypeLabel(type)}
                    </b>{' '}
                    avec l'ensemble de vos collaborateurs. Vous pouvez ajoutez une nouvelle adresse mail et cliquer sur le bouton "<b>PARTAGER</b>"
                  </span>
                </Empty>
              ) : (
                <Empty>
                  <SlUserUnfollow />
                  <span>
                    Vous n'avez <b>pas encore de collaborateur</b>, ajoutez une adresse mail puis cliquez en dessous sur le bouton "<b>PARTAGER</b>"
                    pour partager votre{' '}
                    <b>
                      premier{type !== 'surveyVersions' ? 'e' : ''} {getTypeLabel(type)}
                    </b>
                  </span>
                </Empty>
              )}
              <UserListSlider>
                <UserListContainer>
                  {[...activeAccount?.users]
                    .sort((a, b) => isAlreadyShared(a, type, id) - isAlreadyShared(b, type, id))
                    ?.map(
                      (user, index) =>
                        user && (
                          <UserRow
                            onClick={() => handleSelectUser(user)}
                            key={index}
                            checked={isChecked(user)}
                            alreadyShared={isAlreadyShared(user, type, id)}>
                            <Initial
                              name={user?.name !== '' ? user.name : user?.email}
                              firstName={user?.firstName}
                              color={isAlreadyShared(user, type, id) ? COLORS.Green : null}
                            />
                            <UserItem> {user?.firstName ? user?.name + ' ' + user?.firstName : user?.name || user?.email}</UserItem>
                            {!isAlreadyShared(user, type, id) ? (
                              <input
                                checked={isChecked(user)}
                                onChange={() => handleSelectUser(user)}
                                id={`checkbox-${index}`}
                                name={user?.firstName ? user?.name + ' ' + user?.firstName : user?.name}
                                type={'checkbox'}
                              />
                            ) : (
                              <span>déjà partagé</span>
                            )}
                          </UserRow>
                        )
                    )}
                </UserListContainer>
              </UserListSlider>
              <InputComponent
                type={'text'}
                placeholder={'Ajoutez un commentaire à destination des utilisateur(s) séléctionnés'}
                onChange={handleCommentChange}
                value={commentInput}
              />
              <ButtonContainer>
                <Button label={'ANNULER'} onClick={onClose} />
                <Button disabled={usersChecked.length < 1 && emailInputList.length < 1} label={'PARTAGER'} onClick={handleShare} />
              </ButtonContainer>
            </>
          </ModalShareContainer>
        </Modal>
      )}
    </>
  );
};

const ModalShareContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-family: 'Nunito';
  font-style: normal;
  font-size: 2em;
  flex: 1;
  row-gap: 20px;
  min-height: 0;
`;

const LabelContainer = styled.div`
  display: flex;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Chips = styled.div`
  background-color: #c8e3f6;
  display: inline-flex;
  border-radius: 30px;
  height: 30px;
  font-size: 14px;
  padding: 0 4px 0 1rem;
  align-items: center;
  margin: 0 0.3rem 0.3rem 0;
`;

const ChipsButton = styled.div`
  background-color: white;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font: inherit;
  margin-left: 10px;
  font-weight: bold;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 22px;
`;

const EmailError = styled.div`
  font-size: 1.3rem;
  color: red;
`;

const UserListSlider = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;
const UserListContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const UserRow = styled.div<{ checked: boolean; alreadyShared: boolean }>`
  display: flex;
  column-gap: 20px;
  justify-content: flex-start;
  align-items: center;
  font-family: 'Roboto';
  font-size: 16px;
  width: calc(100% - 30px);
  font-weight: 500;
  padding: 15px;
  background-color: ${({ alreadyShared, checked }) => (alreadyShared ? '#E6FFE6' : checked ? '#C8E3F64D' : 'white')};
  border-bottom: 1px solid rgba(196, 196, 196, 0.37);
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  pointer-events: ${({ alreadyShared }) => (alreadyShared ? 'none' : 'auto')};

  &:hover {
    background-color: #c8e3f64d;
  }

  span {
    font-size: 1.125rem;
    color: ${COLORS.xlsx};
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const UserItem = styled.div`
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  column-gap: 20px;
  font-family: 'Roboto';
`;

const Empty = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  align-self: center;
  text-align: center;
  row-gap: 20px;

  svg {
    height: 40px;
    width: 40px;
    color: ${COLORS.MediumGrey};
    animation: swing 0.8s ease 0.7s;
  }

  span {
    font-size: 1.2rem;
    color: ${COLORS.Grey};
    line-height: 25px;
  }
`;
